<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import type { ITrackerDealForm, ITrackerDealFormComposableOptions, ITrackerDealFormSchema } from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import type { ITrackerDealChangesModalPageProps } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/submodules/tracker-deal-changes/pages/TrackerDealChangesModalPage/types';
import VButton from '@components/buttons/VButton/VButton.vue';
import VLoader from '@components/loader/VLoader.vue';
import {

  useTrackerDealForm,
} from '@modules/affiliates-approved/submodules/trackers-settings/composables';
import { AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/constants';
import { useFormatMonthDate } from '@modules/core/composables/datetime';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { utcDateToLocal } from '@utils/date';
import { isString } from '@utils/guards';

import { TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT } from '../../constants';
import { useTrackerHistoryChangesItemUpdateMutation } from '../../mutations';
import { useTrackerHistoryChangesItemQuery } from '../../queries';

const props = defineProps<Pick<ITrackerDealChangesModalPageProps, 'backFromEditHistoryRoute' | 'merchantName'>>();

const route = useRoute();
const { getTranslation } = useI18n();
const { formatMonthDate } = useFormatMonthDate();

const merchantId = computed(() => (isString(route.params.merchantId) ? Number(route.params.merchantId) : 0));
const itemId = computed(() => (isString(route.params.itemId) ? Number(route.params.itemId) : 0));

const { data, isLoading } = useTrackerHistoryChangesItemQuery(itemId, merchantId);

const {
  mutateAsync,
  isLoading: isUpdateLoading,
  errors,
  clearError,
} = useTrackerHistoryChangesItemUpdateMutation();

const formOptions = computed<ITrackerDealFormComposableOptions>(() => ({
  conditions: data.value?.conditions.all_condition ?? [],
  isLoading: isLoading.value,
  conditionValue: data.value?.history_of_trackers_change.condition_id,
  conditionLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'FORM_CONDITION').value,
  dateLabel: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'FORM_DATE').value,
  dateValue: data.value?.history_of_trackers_change.date ? utcDateToLocal(data.value?.history_of_trackers_change.date) : undefined,
}));

const { formSchema } = useTrackerDealForm(formOptions);

const schema = computed<ITrackerDealFormSchema>(() => {
  const form: ITrackerDealFormSchema = {
    merchant_name: {
      type: EFormInputTypes.TEXT,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'MERCHANT_NAME').value,
      value: props.merchantName,
      readonly: true,
    },
    serial_id: {
      type: EFormInputTypes.NUMBER,
      label: getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'FORM_TRACKER').value,
      value: data.value?.history_of_trackers_change.serial_id,
      useGrouping: false,
      maxFractionDigits: 0,
      readonly: true,
    },
    ...formSchema.value,
  };

  return form;
});

async function onSubmit({ condition_id, date, serial_id }: ITrackerDealForm): Promise<void> {
  await mutateAsync({
    itemId: itemId.value,
    merchant: merchantId.value,
    history_of_trackers_change: {
      id: itemId.value,
      serial_id,
      condition_id,
      date: formatMonthDate(date),
    },
  });
}

function onChange(): void {
  clearError('serial_id');
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'TrackerHistoryChangesEditPage'"
    :loading="isLoading"
    min-height="420px"
  >
    <div class="p-5">
      <h4 class="p-card-title mb-3 text-lg">
        {{ $getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'HEADER') }}
      </h4>
      <VForm
        :schema="schema"
        :submit-btn-label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'SUBMIT')"
        :readonly="!$hasPermission(AFFILIATES_APPROVED_TRACKERS_LIST_PERMISSIONS.HISTORY_CHANGES_EDIT)"
        :is-loading="isUpdateLoading"
        :custom-errors="errors"
        @change="onChange"
        @submit="onSubmit"
        @interacted="clearError"
      >
        <template #secondaryBtn>
          <VButton
            :label="$getTranslation(TRANSLATION_GROUP_AFFILIATES_TRACKERS_SETTINGS_TRACKERS_HISTORY_CHANGES_EDIT, 'CLOSE')"
            :to="backFromEditHistoryRoute"
            outlined
          />
        </template>
      </VForm>
    </div>
  </VLoader>
</template>
